import { LimitName } from '@anm/data/profile/subscriptionLimits';
import bytesToGigaBytes from '@anm/helpers/size/bytesToGigaBytes';
import formatBytes from '@anm/helpers/size/formatBytes';
import formatTime from '@anm/helpers/time/formatTime';
import getRemainingTimeToEndMonth from '@anm/helpers/time/getRemainingTimeToEndMonth';
import { CommonStats } from 'user';

import { TrafficProps } from '../types';

export const getExtraTraffic = ({ used, available, diffBytes }: TrafficProps) => {
  if (diffBytes) return [bytesToGigaBytes(diffBytes).toFixed(2), 'GB'];

  if (!used || !available) return [];

  const [usedValue, strUnit] = used.split(' ');
  const [availableValue] = available.split(' ');

  const unit = strUnit ? strUnit : '';
  const value = +(+usedValue.replace(/,/g, '') - +availableValue.replace(/,/g, '')).toFixed(2);

  return [value, unit] as const;
};

export const getLeftInPercentage = (usedData: number, availableData: number) => {
  const leftInPercentage = 100 - (usedData * 100) / availableData;

  return leftInPercentage > 0 ? leftInPercentage : 0;
};

export const getLeftTraffic = (bytes: number) => {
  const res = bytes > 0 ? bytes : 0;

  return formatBytes({ bytes: res });
};

export const formatSecondsToMinutes = (seconds: number) => {
  if (seconds <= 0) return '0 min';

  const [secs, mins, hours = 0] = formatTime(seconds)
    .split(':')
    .reverse();

  const min = +hours * 60 + +mins;

  const minStr = min ? `${min} min` : '';
  const secStr = +secs || !min ? `${+secs} sec` : '';

  return `${minStr} ${secStr}`.trim();
};

export const getTrafficExtraCharge = (bytes: number, itemCost: number) => {
  if (bytes < 0) return 0;
  const extraTraffic = bytesToGigaBytes(bytes);
  const priceInBucks = (+extraTraffic * itemCost) / 100;

  return +priceInBucks.toFixed(2);
};

export const getCaptionsExtraCharge = (extraSeconds: number, minuteCost: number) => {
  if (extraSeconds < 0) return 0;

  const mins = extraSeconds / 60;
  const price = ((+mins * minuteCost) / 100).toFixed(2);

  return +price;
};

export const getEmbedExtraCharge = (usedData: number, availableData: number, embedCost: number) => {
  const diff = usedData - availableData;
  if (diff < 0) return 0;

  return +((diff * embedCost) / 100).toFixed(2);
};

export const getText2SpeechExtraCharge = (extraChars: number, thousandCharsCost: number) => {
  if (extraChars < 0) return 0;

  const price = (((extraChars / 1000) * thousandCharsCost) / 100).toFixed(2);

  return +price;
};

type LimitDataParams = {
  extraItemCost: number;
  data?: CommonStats;
  canPurchase?: boolean;
};

export const getEmbedStatsData = ({ data, extraItemCost, canPurchase }: LimitDataParams) => {
  const usedData = data?.used || 0;
  const availableData = data?.available || 0;

  return {
    name: LimitName.embed,
    extraItemCost,
    used: `${usedData}`,
    canPurchase: !!canPurchase,
    available: `${availableData}`,
    isWarning: usedData >= availableData,
    left: `${availableData - usedData > 0 ? availableData - usedData : 0}`,
    extraCharge: getEmbedExtraCharge(usedData, availableData, extraItemCost),
    leftInPercentage: getLeftInPercentage(usedData, availableData)
  };
};

export const getStorageStatsData = ({ data, extraItemCost, canPurchase }: LimitDataParams) => {
  const usedData = data?.used || 0;
  const availableData = data?.available || 0;

  return {
    extraItemCost,
    name: LimitName.storage,
    canPurchase: !!canPurchase,
    diffBytes: usedData - availableData,
    isWarning: usedData >= availableData,
    used: `${formatBytes({ bytes: usedData })}`,
    left: `${getLeftTraffic(availableData - usedData)}`,
    available: `${formatBytes({ bytes: availableData })}`,
    leftInPercentage: getLeftInPercentage(usedData, availableData),
    extraCharge: getTrafficExtraCharge(usedData - availableData, extraItemCost)
  };
};

export const getHostingStatsData = ({ data, extraItemCost, canPurchase }: LimitDataParams) => {
  const usedData = data?.used || 0;
  const availableData = data?.available || 0;

  return {
    extraItemCost,
    name: LimitName.traffic,
    canPurchase: !!canPurchase,
    diffBytes: usedData - availableData,
    isWarning: usedData >= availableData,
    used: `${formatBytes({ bytes: usedData })}`,
    resetInTime: getRemainingTimeToEndMonth(),
    left: `${getLeftTraffic(availableData - usedData)}`,
    available: `${formatBytes({ bytes: availableData })}`,
    leftInPercentage: getLeftInPercentage(usedData, availableData),
    extraCharge: getTrafficExtraCharge(usedData - availableData, extraItemCost)
  };
};

export const getTranscribeStatsData = ({ data, extraItemCost, canPurchase }: LimitDataParams) => {
  const usedData = data?.used || 0;
  const availableData = data?.available || 0;

  return {
    extraItemCost,
    name: LimitName.captions,
    canPurchase: !!canPurchase,
    isWarning: usedData >= availableData,
    used: formatSecondsToMinutes(usedData),
    resetInTime: getRemainingTimeToEndMonth(),
    left: formatSecondsToMinutes(availableData - usedData),
    available: formatSecondsToMinutes(availableData),
    leftInPercentage: getLeftInPercentage(usedData, availableData),
    extraCharge: getCaptionsExtraCharge(usedData - availableData, extraItemCost)
  };
};

export const getText2SpeechData = ({ data, extraItemCost, canPurchase }: LimitDataParams) => {
  const usedData = data?.used || 0;
  const availableData = data?.available || 0;

  return {
    extraItemCost,
    canPurchase: !!canPurchase,
    name: LimitName.text2speech,
    available: `${availableData.toLocaleString()} symbol`,
    isWarning: usedData >= availableData,
    resetInTime: getRemainingTimeToEndMonth(),
    used: `${usedData.toLocaleString()} symbol`,
    left: availableData - usedData > 0 ? `${(availableData - usedData).toLocaleString()} symbol` : '0 symbol',
    leftInPercentage: getLeftInPercentage(usedData, availableData),
    extraCharge: getText2SpeechExtraCharge(usedData - availableData, extraItemCost)
  };
};
